import { create } from 'zustand'

export type NotificationKind = 'success' | 'error' | 'loading' | 'blank' | 'custom'

export interface NotificationHistoryItem {
  id: string
  message: string
  type: NotificationKind
  createdAt: number
  read: boolean
}

interface NotificationHistoryState {
  items: NotificationHistoryItem[]
  addOrUpdate: (item: Omit<NotificationHistoryItem, 'read'>) => void
  markAllRead: () => void
  clearAll: () => void
}

const MAX_ITEMS = 30
/** Collapse duplicate toasts (e.g. API interceptor + page handler) within this window. */
const DEDUPE_WINDOW_MS = 5000

export const useNotificationHistoryStore = create<NotificationHistoryState>((set) => ({
  items: [],

  addOrUpdate: (item) =>
    set((state) => {
      const existingIndex = state.items.findIndex((i) => i.id === item.id)
      if (existingIndex >= 0) {
        const previous = state.items[existingIndex]!
        if (previous.message === item.message && previous.type === item.type) {
          return state
        }
        const updated = [...state.items]
        updated[existingIndex] = {
          ...previous,
          message: item.message,
          type: item.type,
          createdAt: item.createdAt || previous.createdAt,
        }
        return { items: updated.sort((a, b) => b.createdAt - a.createdAt).slice(0, MAX_ITEMS) }
      }

      const createdAt = item.createdAt || Date.now()
      const duplicateIndex = state.items.findIndex(
        (i) =>
          i.message === item.message &&
          i.type === item.type &&
          Math.abs(i.createdAt - createdAt) < DEDUPE_WINDOW_MS,
      )
      if (duplicateIndex >= 0) {
        return state
      }

      return {
        items: [{ ...item, createdAt, read: false }, ...state.items].slice(0, MAX_ITEMS),
      }
    }),

  markAllRead: () =>
    set((state) => ({
      items: state.items.map((item) => ({ ...item, read: true })),
    })),

  clearAll: () => set({ items: [] }),
}))
